import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Grid
} from '@mui/material'
import { 
  Delete, 
  ContentCopy, 
  Link, 
  CalendarToday, 
  Add, 
  Visibility, 
  VisibilityOff,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Cancel
} from '@mui/icons-material'
import { useAccessCodes } from 'hooks/useAccessCodes'
import { AccessCode } from '@otw/models/photos'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { usePhotoUploadAccess } from 'hooks/usePhotoUploadAccess'
import { UserPhotoUploadSummary } from 'hooks/admin/usePhotoUploads'
import { useIsMobile } from 'hooks/screen/useIsMobile'

interface ManageAccessCodesDialogProps {
  open: boolean
  onClose: () => void
  userSummary: UserPhotoUploadSummary | null
}

const ManageAccessCodesDialog: React.FC<ManageAccessCodesDialogProps> = ({
  open,
  onClose,
  userSummary,
}) => {
  const { showSuccess, showError } = useSnackbar()
  const isMobile = useIsMobile()
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({})
  const [isCreatingNew, setIsCreatingNew] = useState(false)
  const [newAccessCode, setNewAccessCode] = useState('')
  const [selectedPhotoUpload, setSelectedPhotoUpload] = useState<string>('')
  
  // Get all access codes and filter for this user
  const {
    accessCodes,
    isLoading: isLoadingAccessCodes,
    deleteAccessCode,
    isDeletingAccessCode,
    getAccessLink,
    refetch: refetchAccessCodes
  } = useAccessCodes()

  // Access code creation hook
  const {
    createAccessCode,
    loading: isCreatingAccessCode
  } = usePhotoUploadAccess()

  // Filter access codes for this user's photo upload
  const userAccessCodes = accessCodes.filter(code => 
    code.photoUploadId === userSummary?.photoUploadId
  )

  // Set default selected photo upload
  useEffect(() => {
    if (userSummary?.photoUploadId && !selectedPhotoUpload) {
      setSelectedPhotoUpload(userSummary.photoUploadId)
    }
  }, [userSummary, selectedPhotoUpload])

  if (!userSummary) return null

  const isLoading = isLoadingAccessCodes || isDeletingAccessCode || isCreatingAccessCode

  const togglePasswordVisibility = (accessCodeId: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [accessCodeId]: !prev[accessCodeId]
    }))
  }

  const handleCopyPassword = (password: string) => {
    navigator.clipboard.writeText(password)
    showSuccess('Password copied to clipboard')
  }

  const handleCopyLink = (accessCodeUuid: string) => {
    const link = getAccessLink(accessCodeUuid)
    navigator.clipboard.writeText(link)
    showSuccess('Link copied to clipboard')
  }

  const handleDeleteAccessCode = async (accessCodeId: string) => {
    try {
      await deleteAccessCode(accessCodeId)
      await refetchAccessCodes()
    } catch (error) {
      console.error('Error deleting access code:', error)
      showError('Error deleting access code')
    }
  }

  const handleCreateNewAccessCode = async () => {
    if (!selectedPhotoUpload || !newAccessCode.trim()) {
      showError('Please select a photo upload and enter an access code')
      return
    }

    try {
      const photoUpload = userSummary.photoUploadDocument
      if (!photoUpload || photoUpload.id !== selectedPhotoUpload) {
        showError('Selected photo upload not found')
        return
      }

      // Get vehicle info for the selected photo upload
      const vehicles = userSummary.vehicles
        .filter(vehicle => photoUpload.vehicleIds.includes(vehicle.id))
        .map(vehicle => ({
          id: vehicle.id,
          vin: vehicle.vin,
          make: vehicle.make || '',
          model: vehicle.model || '',
          year: vehicle.year?.toString() || ''
        }))

      await createAccessCode({
        accessCode: newAccessCode,
        userId: userSummary.user.id,
        vehicles,
        photoUploadId: selectedPhotoUpload
      })

      await refetchAccessCodes()
      setIsCreatingNew(false)
      setNewAccessCode('')
      showSuccess('New access code created successfully')
    } catch (error) {
      console.error('Error creating access code:', error)
      showError('Error creating access code')
    }
  }

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date()
  }

  const getStatusColor = (code: AccessCode) => {
    if (isExpired(code.expires)) return 'error'
    const expiresIn = new Date(code.expires).getTime() - new Date().getTime()
    const hoursUntilExpiry = expiresIn / (1000 * 60 * 60)
    if (hoursUntilExpiry < 24) return 'warning'
    return 'success'
  }

  const getStatusText = (code: AccessCode) => {
    if (isExpired(code.expires)) return 'Expired'
    const expiresIn = new Date(code.expires).getTime() - new Date().getTime()
    const hoursUntilExpiry = expiresIn / (1000 * 60 * 60)
    if (hoursUntilExpiry < 1) return 'Expires in < 1 hour'
    if (hoursUntilExpiry < 24) return `Expires in ${Math.round(hoursUntilExpiry)} hours`
    const daysUntilExpiry = Math.round(hoursUntilExpiry / 24)
    return `Expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}`
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>
        <Box>
          <Typography variant="h6">Manage Photo Upload Access</Typography>
          <Typography variant="body2" color="text.secondary">
            {userSummary.user.email}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            This user has 1 photo upload request 
            covering {userSummary.totalVehicles} vehicle(s) with{' '}
            {userSummary.actualPhotoCount !== undefined 
              ? `${userSummary.actualPhotoCount}/${userSummary.expectedPhotoCount} photos uploaded`
              : `${userSummary.submissionCount} completed submissions`}.
          </Typography>
        </Box>

        {/* Current Access Codes */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Current Access Codes ({userAccessCodes.length})
          </Typography>
          
          {userAccessCodes.length === 0 ? (
            <Alert severity="info">No access codes found for this user.</Alert>
          ) : (
            <List>
              {userAccessCodes.map((code, index) => {
                const photoUpload = userSummary.photoUploadDocument
                const statusColor = getStatusColor(code)
                const statusText = getStatusText(code)
                const expired = isExpired(code.expires)
                
                return (
                  <React.Fragment key={code.id}>
                    <ListItem sx={{ py: 2 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                Password:
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontFamily: 'monospace',
                                  backgroundColor: 'grey.100',
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1
                                }}
                              >
                                {showPasswords[code.id] ? code.code : '••••••••'}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => togglePasswordVisibility(code.id)}
                              >
                                {showPasswords[code.id] ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </Box>
                            <Chip
                              size="small"
                              label={statusText}
                              color={statusColor}
                              icon={
                                statusColor === 'error' ? <ErrorIcon /> :
                                statusColor === 'warning' ? <Warning /> : <CheckCircle />
                              }
                              sx={{ fontSize: '0.75rem' }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <CalendarToday sx={{ fontSize: '0.8rem' }} />
                              <Typography variant="caption">
                                Created: {new Date(code.createdAt).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Vehicles: {code.vehicles?.length || 0} | 
                              Photo Upload: {photoUpload?.id.substring(0, 8) || 'Unknown'}
                            </Typography>
                            {expired && (
                              <Typography variant="caption" color="error" display="block">
                                ⚠️ This access code has expired. Create a new one below.
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyPassword(code.code)}
                            title="Copy password"
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleCopyLink(code.id)}
                            title="Copy access link"
                          >
                            <Link fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteAccessCode(code.id)}
                            disabled={isLoading}
                            title="Delete this access code"
                          >
                            {isLoading ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
                          </IconButton>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < userAccessCodes.length - 1 && <Divider />}
                  </React.Fragment>
                )
              })}
            </List>
          )}
        </Box>

        {/* Create New Access Code */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Create New Access Code</Typography>
              <Button
                variant="outlined"
                startIcon={isCreatingNew ? <Cancel /> : <Add />}
                onClick={() => setIsCreatingNew(!isCreatingNew)}
              >
                {isCreatingNew ? 'Cancel' : 'Create New'}
              </Button>
            </Box>

            {isCreatingNew && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Alert severity="info">
                    Creating access code for photo upload: {userSummary.photoUploadDocument.id.substring(0, 8)} 
                    ({userSummary.photoUploadDocument.vehicleIds.length} vehicle(s))
                  </Alert>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Access Code (Password)"
                    value={newAccessCode}
                    onChange={(e) => setNewAccessCode(e.target.value)}
                    helperText="This is the password users will enter to access the upload"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    onClick={handleCreateNewAccessCode}
                    disabled={isLoading || !selectedPhotoUpload || !newAccessCode.trim()}
                    startIcon={isLoading ? <CircularProgress size={16} /> : <Add />}
                  >
                    Create Access Code
                  </Button>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default ManageAccessCodesDialog 